/**
 * A queue for a capture made with no connection: it holds exactly what
 * `Capture.tsx`'s `save()` would otherwise have sent -- the item request and
 * the already-resized photo blobs -- in IndexedDB (the one browser storage
 * that can hold a `Blob` without re-encoding it first), and sends it once the
 * connection is back.
 *
 * Two failure modes the roadmap named for this feature, and how each is held
 * off:
 *
 * - **A duplicate item.** An item's id is minted by the worker, not chosen by
 *   the client, and there is no idempotency key -- calling `create` twice
 *   makes two items. Every queued entry remembers the id the *first*
 *   successful `create` returned (`itemId`) and how many of its photos have
 *   already landed (`uploaded`), so a retry -- whether from a dropped
 *   connection mid-upload or a second drain later -- resumes rather than
 *   repeats the create.
 * - **A queue that outlives a sign-out.** This module only ever drains into
 *   whichever inventory is active *right now* (`drainQueue` below); pairing
 *   that with `useSession.ts`'s `signOut()` calling `clearQueue()` on a
 *   successful sign-out means a second person on the same device never
 *   inherits the first person's unsent captures.
 *
 * Every export here is defensive the same way `lib/capture.ts` and
 * `lib/activeInventory.ts` already are: no IndexedDB (a locked-down browser,
 * a private window on some of them) or a write that fails never throws past
 * this module, it just means the queue does not stick -- the caller still has
 * whatever it was about to hand this module in the first place.
 */

import type { ItemWriteRequest } from "@iltocat/shared";
import { ApiError, api } from "./api.js";
import { getActiveInventory } from "./activeInventory.js";

const DB_NAME = "iltocat-offline";
const STORE = "captures";
/** Fired on this module's own mutations, for whatever wants to show a live count -- see `useOfflineQueue.ts`. */
const CHANGED_EVENT = "iltocat:offline-queue-changed";

export interface QueuedPhoto {
  full: Blob;
  thumb: Blob;
  width: number;
  height: number;
}

export interface QueuedCapture {
  id: string;
  /** The inventory this was queued for. A switch always reloads the page (`activeInventory.ts`), so this can only ever disagree with "active right now" across a reload -- and when it does, draining leaves the entry alone rather than sending it to the wrong inventory. */
  inventoryId: string | null;
  request: ItemWriteRequest;
  /** Only the photos not yet confirmed uploaded -- one already sent is not kept a second time. */
  photos: QueuedPhoto[];
  /** Set once `create` has succeeded, so a retry resumes the photo uploads instead of repeating it. */
  itemId: string | null;
  /** How many of `photos`, from the start, have already uploaded. */
  uploaded: number;
  queuedAt: number;
  /** Set when the worker refused the request for a reason a retry will not fix. Left in the queue and visible rather than dropped; draining skips it. */
  lastError: string | null;
}

function hasIndexedDb(): boolean {
  return typeof window !== "undefined" && !!window.indexedDB;
}

/**
 * `crypto.randomUUID()` only exists in a secure context -- utucat is always
 * HTTPS in production, so this only ever falls back somewhere it should not
 * matter (an insecure preview, this codebase's own local dev harness). A
 * queue key only has to be unique on this one device, not globally, so the
 * fallback does not need to be a real UUID.
 */
function newId(): string {
  if (typeof window.crypto?.randomUUID === "function") return window.crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = window.indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: "id" });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openDb();
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const req = run(tx.objectStore(STORE));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  } finally {
    db.close();
  }
}

function notifyChanged(): void {
  try {
    window.dispatchEvent(new Event(CHANGED_EVENT));
  } catch {
    // No window to tell, or a browser that refuses -- nothing else reads this.
  }
}

/** Whenever a mutation here changes what a badge should show. Does not itself fire on reconnecting -- see `useOfflineQueue.ts` for that. */
export function onQueueChanged(listener: () => void): () => void {
  if (typeof window === "undefined") return () => undefined;
  window.addEventListener(CHANGED_EVENT, listener);
  return () => window.removeEventListener(CHANGED_EVENT, listener);
}

/** Queues one capture. `itemId` is set only when `create` already succeeded before the connection dropped mid-upload -- see the module doc. */
export async function enqueueCapture(
  request: ItemWriteRequest,
  photos: QueuedPhoto[],
  itemId: string | null = null,
): Promise<void> {
  if (!hasIndexedDb()) return;
  const entry: QueuedCapture = {
    id: newId(),
    inventoryId: getActiveInventory(),
    request,
    photos,
    itemId,
    uploaded: 0,
    queuedAt: Date.now(),
    lastError: null,
  };
  try {
    await withStore("readwrite", (store) => store.add(entry));
    notifyChanged();
  } catch {
    // The capture screen still has the draft and photos on screen either way.
  }
}

export async function queueCount(): Promise<number> {
  if (!hasIndexedDb()) return 0;
  try {
    return await withStore("readonly", (store) => store.count());
  } catch {
    return 0;
  }
}

/** Everything queued on this device, whichever account it was queued under -- signing out is the one thing that has to answer that question, not this module (see `useSession.ts`). */
export async function clearQueue(): Promise<void> {
  if (!hasIndexedDb()) return;
  try {
    await withStore("readwrite", (store) => store.clear());
    notifyChanged();
  } catch {
    // Nothing to clear if it could not be reached either.
  }
}

/**
 * `useOfflineQueue` calls this on mount and on every `online` event, and both
 * can happen within the same tick of each other (a tab's mount racing a
 * connection that is already back, or the browser firing `online` more than
 * once while it is flapping) -- two concurrent passes would both read the
 * same not-yet-created entry and both call `create`, which is the duplicate
 * item this whole module exists to prevent. A drain already running is
 * therefore handed out again rather than started twice.
 */
interface DrainResult {
  sent: number;
  remaining: number;
  /** The account's storage after the last photo this drain actually uploaded -- unset if none did. A background upload the capture screen never saw would otherwise leave the sidebar's own figure stale until something else happened to refresh it. */
  usedBytes?: number;
}

let inFlight: Promise<DrainResult> | null = null;

export function drainQueue(): Promise<DrainResult> {
  if (!inFlight) inFlight = runDrain().finally(() => (inFlight = null));
  return inFlight;
}

/**
 * Sends everything queued for the inventory this device is on right now, in
 * the order it was queued. Stops at the first network failure rather than
 * skipping ahead out of order -- a shelf should finish uploading in the order
 * it was captured, not whichever items happen to get signal first.
 *
 * A refusal that is not a network failure (the worker explained why) marks
 * the entry with `lastError` and moves on to the next one: a retry would not
 * change the worker's mind, and the point of `lastError` is that the entry
 * stays visible instead of silently vanishing or silently retrying forever.
 */
async function runDrain(): Promise<DrainResult> {
  if (!hasIndexedDb()) return { sent: 0, remaining: 0 };

  const active = getActiveInventory();
  let entries: QueuedCapture[];
  try {
    entries = await withStore("readonly", (store) => store.getAll());
  } catch {
    return { sent: 0, remaining: await queueCount() };
  }
  entries.sort((a, b) => a.queuedAt - b.queuedAt);

  let sent = 0;
  let usedBytes: number | undefined;
  for (const entry of entries) {
    if (entry.inventoryId !== active || entry.lastError) continue;

    try {
      let itemId = entry.itemId;
      if (!itemId) {
        const { item } = await api.items.create(entry.request);
        itemId = item.id;
        entry.itemId = itemId;
        await withStore("readwrite", (store) => store.put(entry));
      }

      while (entry.uploaded < entry.photos.length) {
        const photo = entry.photos[entry.uploaded]!;
        usedBytes = (await api.items.addPhoto(itemId, photo)).usedBytes;
        entry.uploaded += 1;
        await withStore("readwrite", (store) => store.put(entry));
      }

      await withStore("readwrite", (store) => store.delete(entry.id));
      sent += 1;
    } catch (problem) {
      if (problem instanceof ApiError && problem.status === 0) break; // still offline -- try the rest next time, in order

      entry.lastError = problem instanceof ApiError ? problem.message : "That did not save.";
      try {
        await withStore("readwrite", (store) => store.put(entry));
      } catch {
        // The next drain will see it as it was before this attempt and try again, which is safe either way.
      }
    }
  }

  const remaining = await queueCount();
  notifyChanged();
  return usedBytes === undefined ? { sent, remaining } : { sent, remaining, usedBytes };
}
